package navigation.home.dashboard

import addedittravelagent.AddEditTravelAgentEvent
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.type
import androidx.compose.ui.unit.dp
import cafe.adriel.voyager.koin.koinScreenModel
import cafe.adriel.voyager.navigator.LocalNavigator
import cafe.adriel.voyager.navigator.Navigator
import cafe.adriel.voyager.navigator.currentOrThrow
import com.essntl.components.sidepanel.SidePanel
import com.essntl.components.sidepanel.SizeConstraint
import com.essntl.components.sidepanel.Splitter
import com.essntl.components.sidepanel.rememberSidePanelState
import com.essntl.features.client.domain.model.ClientModel
import com.essntl.features.client.ui.AddEditClientDialog
import com.essntl.features.client.ui.addeditclient.AddEditClientEvent
import com.essntl.features.dashboard.ui.DashboardEvent
import com.essntl.features.dashboard.ui.DashboardNavigation
import com.essntl.features.dashboard.ui.DashboardScreen
import com.essntl.features.dashboard.ui.DashboardState
import com.essntl.features.itinerary.ui.edit_itinerary.EditItineraryEvent
import com.essntl.features.itinerary.ui.edit_itinerary.EditItineraryState
import com.essntl.features.itinerary.ui.edit_itinerary.components.ItineraryServicesList
import com.essntl.features.request.ui.addeditrequest.AddEditRequestEvent
import com.essntl.features.request.ui.addeditrequest.AddEditRequestNavigation
import com.essntl.features.request.ui.addeditrequest.AddEditRequestScreen
import com.essntl.features.request.ui.addeditrequest.AddEditRequestState
import com.essntl.features.request.ui.addeditrequest.components.AddEditTravelAgentDialog
import com.essntl.features.task.ui.addedittask.AddEditTaskEvent
import com.essntl.features.task.ui.addedittask.AddEditTaskNavigation
import com.essntl.features.task.ui.addedittask.AddEditTaskScreen
import com.essntl.features.task.ui.addedittask.AddEditTaskState
import com.essntl.features.travelagent.domain.model.TravelAgentModel
import essntl.key.LocalKeyEventManager
import com.essntl.uikit.components.button.secondary.EsLargeSecondaryButton
import com.essntl.uikit.components.iconbutton.outline.EsMediumOutlineIconButton
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.link.NavLinkState
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography
import kotlinx.coroutines.flow.collectLatest
import navigation.home.clients.addeditclient.AddEditClientScreenModel
import navigation.home.edititinerary.EditItineraryScreenModel
import navigation.home.edititinerary.EditItineraryScreenNav
import navigation.home.proposal.addeditproposal.AddEditProposalScreenNav
import navigation.home.request.addeditrequest.AddEditRequestScreenModel
import navigation.home.task.addedittask.AddEditTaskScreenModel
import navigation.home.travelagents.addedittravelagent.AddEditTravelAgentScreenModel
import navigation.utils.NavLinkScreen
import navigation.utils.safePush

object DashboardScreenNav : NavLinkScreen {
    override val defaultTitle: String = "Home"

    override var navLinkState: NavLinkState by mutableStateOf(
        NavLinkState(
            title = defaultTitle,
            iconRes = EsIcons.Home,
        ),
    )

    @Composable
    override fun Content() {
        val dashboardScreenModel = koinScreenModel<DashboardScreenModel>()
        val dashboardState by dashboardScreenModel.state.collectAsState()

        val addEditRequestScreenModel = koinScreenModel<AddEditRequestScreenModel>()
        val addEditRequestState by addEditRequestScreenModel.state.collectAsState()

        val addEditTaskScreenModel = koinScreenModel<AddEditTaskScreenModel>()
        val addEditTaskState by addEditTaskScreenModel.state.collectAsState()

        val editItineraryScreenModel = koinScreenModel<EditItineraryScreenModel>()
        val editItineraryState by editItineraryScreenModel.state.collectAsState()

        val selectedId = remember(
            addEditRequestState.getRequest.data?.id,
            addEditTaskState.getTask.data?.id,
        ) {
            addEditRequestState.getRequest.data?.id ?: addEditTaskState.getTask.data?.id
        }

        val navigator = LocalNavigator.currentOrThrow
        val keyEventManager = LocalKeyEventManager.current

        val sidePanelState =
            rememberSidePanelState(
                sidePanelVisible = false,
                panelSizeConstraint =
                    SizeConstraint(
                        minSize = 300.dp,
                        maxSize = 600.dp,
                    ),
            )

        LaunchedEffect(keyEventManager) {
            keyEventManager.events.collectLatest {
                if (it.key == Key.H && it.type == KeyEventType.KeyUp) {
                    sidePanelState.sidePanelOpened = false
                }
            }
        }

        LaunchedEffect(sidePanelState.sidePanelOpened) {
            if (!sidePanelState.sidePanelOpened) {
                addEditRequestScreenModel.onEvent(AddEditRequestEvent.ClearRequestState)
                addEditTaskScreenModel.onEvent(AddEditTaskEvent.ClearTaskState)
            }
        }

        LaunchedEffect(addEditRequestState.addEditRequest) {
            if (addEditRequestState.addEditRequest.isSuccess) {
                addEditRequestState.addEditRequest.data?.let { request ->
                    dashboardScreenModel.onEvent(DashboardEvent.UpdateRequest(request))
                }
                addEditRequestScreenModel.onEvent(AddEditRequestEvent.ClearSuccessState)
            }
        }

        LaunchedEffect(addEditTaskState.addEditTask) {
            if (addEditTaskState.addEditTask.isSuccess) {
                addEditTaskState.addEditTask.data?.let { task ->
                    dashboardScreenModel.onEvent(DashboardEvent.UpdateTask(task))
                }
                addEditTaskScreenModel.onEvent(AddEditTaskEvent.ClearSuccessState)
            }
        }

        LaunchedEffect(Unit) {
            dashboardScreenModel.onEvent(DashboardEvent.LoadItems)
            navLinkState = navLinkState.copy(
                onRefresh = {
                    dashboardScreenModel.onEvent(DashboardEvent.LoadItems)
                    sidePanelState.sidePanelOpened = false
                },
            )
        }

        SidePanel(
            sidePanelState = sidePanelState,
            isHorizontal = true,
            sidePanel = {
                Column(
                    modifier =
                        Modifier
                            .fillMaxHeight()
                            .padding(end = 1.5f.spacing)
                            .clip(RoundedCornerShape(EsConstants.SectionBorderRadius)),
                ) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.SpaceBetween,
                        modifier =
                            Modifier
                                .background(MaterialTheme.esColorScheme.bgSecondary)
                                .padding(2.5f.spacing)
                                .fillMaxWidth(),
                    ) {
                        EsMediumOutlineIconButton(
                            onClick = {
                                sidePanelState.sidePanelOpened = false
                            },
                            iconRes = EsIcons.ArrowRight,
                        )

                        DashboardSidePanelHeader(
                            addEditRequestState = addEditRequestState,
                            addEditTaskState = addEditTaskState,
                            editItineraryState = editItineraryState,
                        )
                    }

                    DashboardSidePanelContent(
                        dashboardScreenModel = dashboardScreenModel,
                        dashboardState = dashboardState,
                        addEditRequestState = addEditRequestState,
                        addEditRequestScreenModel = addEditRequestScreenModel,
                        addEditTaskState = addEditTaskState,
                        addEditTaskScreenModel = addEditTaskScreenModel,
                        editItineraryScreenModel = editItineraryScreenModel,
                        editItineraryState = editItineraryState,
                        navigator = navigator,
                        closeSidePanel = {
                            sidePanelState.sidePanelOpened = false
                        },
                    )
                }
            },
            splitter =
                Splitter(
                    measuredPart = {
                        Box(
                            modifier =
                                Modifier
                                    .width(1.spacing)
                                    .fillMaxHeight(),
                        )
                    },
                ),
            modifier =
                Modifier
                    .fillMaxSize(),
        ) {
            DashboardScreen(
                state = dashboardState,
                selectedId = selectedId,
                onEvent = dashboardScreenModel::onEvent,
                onNavigation = { event ->
                    when (event) {
                        is DashboardNavigation.EditProposal -> {
                            sidePanelState.sidePanelOpened = false
                            navigator.safePush(
                                AddEditProposalScreenNav(
                                    proposalId = event.proposalId,
                                ),
                            )
                        }

                        is DashboardNavigation.EditRequest -> {
                            addEditTaskScreenModel.onEvent(AddEditTaskEvent.ClearTaskState)
                            editItineraryScreenModel.onEvent(EditItineraryEvent.ClearItineraryState)
                            if (
                                sidePanelState.sidePanelOpened &&
                                addEditRequestState.getRequest.data?.id == event.requestId
                            ) {
                                return@DashboardScreen
                            }
                            addEditRequestScreenModel.onEvent(AddEditRequestEvent.ClearRequestState)
                            addEditRequestScreenModel.onEvent(AddEditRequestEvent.GetRequest(event.requestId))
                            sidePanelState.sidePanelOpened = true
                        }

                        is DashboardNavigation.EditTask -> {
                            addEditRequestScreenModel.onEvent(AddEditRequestEvent.ClearRequestState)
                            editItineraryScreenModel.onEvent(EditItineraryEvent.ClearItineraryState)
                            if (
                                sidePanelState.sidePanelOpened &&
                                addEditTaskState.getTask.data?.id == event.taskId
                            ) {
                                return@DashboardScreen
                            }

                            addEditTaskScreenModel.onEvent(AddEditTaskEvent.ClearTaskState)
                            addEditTaskScreenModel.onEvent(AddEditTaskEvent.GetTask(event.taskId))
                            sidePanelState.sidePanelOpened = true
                        }

                        is DashboardNavigation.EditItineraryServices -> {
                            addEditTaskScreenModel.onEvent(AddEditTaskEvent.ClearTaskState)
                            addEditRequestScreenModel.onEvent(AddEditRequestEvent.ClearRequestState)
                            if (
                                sidePanelState.sidePanelOpened &&
                                editItineraryState.getAllItinerary.data?.firstOrNull()?.id == event.itineraryId
                            ) {
                                return@DashboardScreen
                            }
                            editItineraryScreenModel.onEvent(EditItineraryEvent.ClearItineraryState)
                            editItineraryScreenModel.onEvent(EditItineraryEvent.GetItinerary(event.itineraryId))
                            sidePanelState.sidePanelOpened = true
                        }
                    }
                },
            )
        }
    }


    @Composable
    private fun RowScope.DashboardSidePanelHeader(
        addEditRequestState: AddEditRequestState,
        addEditTaskState: AddEditTaskState,
        editItineraryState: EditItineraryState,
    ) {
        if (addEditRequestState.getRequest.data != null) {
            SelectionContainer {
                Text(
                    text = addEditRequestState.request.value.title ?: "",
                    color = MaterialTheme.esColorScheme.textPrimary,
                    style = MaterialTheme.esTypography.h3Bold24,
                )
            }
        } else if (addEditTaskState.getTask.data != null) {
            SelectionContainer {
                Text(
                    text = addEditTaskState.task.value.title ?: "",
                    color = MaterialTheme.esColorScheme.textPrimary,
                    style = MaterialTheme.esTypography.h3Bold24,
                )
            }
        } else if (editItineraryState.getAllItinerary.data != null) {
            SelectionContainer {
                Column(
                    horizontalAlignment = Alignment.End,
                ) {
                    Text(
                        text = editItineraryState.itineraryTitle,
                        color = MaterialTheme.esColorScheme.textPrimary,
                        style = MaterialTheme.esTypography.h3Bold24,
                    )

                    Text(
                        text = editItineraryState.confirmedServicesText,
                        style = MaterialTheme.esTypography.p2Medium14.copy(
                            color = MaterialTheme.esColorScheme.textSecondary,
                        ),
                    )
                }
            }
        }
    }


    @Composable
    private fun DashboardSidePanelContent(
        dashboardScreenModel: DashboardScreenModel,
        dashboardState: DashboardState,
        addEditRequestState: AddEditRequestState,
        addEditRequestScreenModel: AddEditRequestScreenModel,
        addEditTaskState: AddEditTaskState,
        addEditTaskScreenModel: AddEditTaskScreenModel,
        editItineraryScreenModel: EditItineraryScreenModel,
        editItineraryState: EditItineraryState,
        navigator: Navigator,
        closeSidePanel: () -> Unit,
    ) {
        if (addEditRequestState.getRequest.data != null) {
            val addEditTravelAgentScreenModel = koinScreenModel<AddEditTravelAgentScreenModel>()
            val addEditTravelAgentState by addEditTravelAgentScreenModel.state.collectAsState()
            var openAddEditTravelAgentDialog by rememberSaveable { mutableStateOf(false) }
            var taToEdit by remember { mutableStateOf<TravelAgentModel?>(null) }

            val addEditClientScreenModel = koinScreenModel<AddEditClientScreenModel>()
            val addEditClientState by addEditClientScreenModel.state.collectAsState()
            var openAddEditClientDialog by rememberSaveable { mutableStateOf(false) }
            var clientToEdit by remember { mutableStateOf<ClientModel?>(null) }

            LaunchedEffect(addEditTravelAgentState.addEditTravelAgent.isSuccess) {
                if (addEditTravelAgentState.addEditTravelAgent.isSuccess) {
                    addEditTravelAgentState.addEditTravelAgent.data?.let {
                        if (taToEdit == null) {
                            addEditRequestScreenModel.onEvent(AddEditRequestEvent.AddTravelAgent(it))
                        }
                    }
                    addEditTravelAgentScreenModel.onEvent(AddEditTravelAgentEvent.ClearTravelAgentState)
                    openAddEditTravelAgentDialog = false
                    taToEdit = null
                }
            }

            LaunchedEffect(addEditClientState.addEditClient.isSuccess) {
                if (addEditClientState.addEditClient.isSuccess) {
                    addEditClientState.addEditClient.data?.let {
                        if (clientToEdit != null) {
                            addEditRequestScreenModel.onEvent(AddEditRequestEvent.EditClient(it))
                        } else {
                            addEditRequestScreenModel.onEvent(AddEditRequestEvent.AddClient(it))
                        }
                    }
                    addEditClientScreenModel.onEvent(AddEditClientEvent.ClearClientState)
                    openAddEditClientDialog = false
                    clientToEdit = null
                }
            }

            AddEditRequestScreen(
                state = addEditRequestState,
                validation = addEditRequestScreenModel.validation,
                onEvent = addEditRequestScreenModel::onEvent,
                getAllRequest = dashboardState.getAllRequest.data.orEmpty(),
                onUpdateRequestStatus = { requestId, status ->
                    dashboardScreenModel.onEvent(
                        DashboardEvent.UpdateRequestStatus(requestId, status),
                    )
                },
                onNavigation = {
                    when (it) {
                        is AddEditRequestNavigation.AddClient -> {
                            openAddEditClientDialog = true
                        }

                        is AddEditRequestNavigation.EditClient -> {
                            clientToEdit = it.client
                            openAddEditClientDialog = true
                        }

                        is AddEditRequestNavigation.AddTravelAgent -> {
                            openAddEditTravelAgentDialog = true
                        }

                        is AddEditRequestNavigation.EditTravelAgent -> {
                            taToEdit = it.travelAgent
                            openAddEditTravelAgentDialog = true
                        }
                    }
                },
            )

            AddEditTravelAgentDialog(
                openDialog = openAddEditTravelAgentDialog,
                currentTravelAgent = taToEdit,
                state = addEditTravelAgentState,
                onEvent = addEditTravelAgentScreenModel::onEvent,
                onDismissRequest = {
                    openAddEditTravelAgentDialog = false
                    taToEdit = null
                },
            )

            AddEditClientDialog(
                openDialog = openAddEditClientDialog,
                currentClient = clientToEdit,
                state = addEditClientState,
                onEvent = addEditClientScreenModel::onEvent,
                validation = addEditClientScreenModel.validation,
                onDismissRequest = {
                    openAddEditClientDialog = false
                    clientToEdit = null
                },
            )
        } else if (addEditTaskState.getTask.data != null) {
            AddEditTaskScreen(
                state = addEditTaskState,
                onEvent = addEditTaskScreenModel::onEvent,
                validation = addEditTaskScreenModel.validation,
                getAllTask = dashboardState.getAllTask.data,
                onUpdateTaskStatus = { taskId, status ->
                    dashboardScreenModel.onEvent(
                        DashboardEvent.UpdateTaskStatus(taskId, status),
                    )
                },
                onNavigation = {
                    when (it) {
                        is AddEditTaskNavigation.EditItinerary -> {
                            navigator.safePush(
                                EditItineraryScreenNav(
                                    itineraryId = it.itineraryId,
                                ),
                            )
                        }
                    }
                },
            )
        } else if (editItineraryState.getAllItinerary.data != null) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .clip(
                        RoundedCornerShape(
                            bottomStart = 24.dp,
                            bottomEnd = 24.dp,
                        ),
                    )
                    .background(MaterialTheme.esColorScheme.bgSecondary)
                    .padding(
                        top = 1.5f.spacing,
                        bottom = 2.5f.spacing,
                        end = 2.5f.spacing,
                        start = 2.5f.spacing,
                    ),
            ) {
                EsLargeSecondaryButton(
                    text = "Go to itinerary",
                    prefixIconRes = EsIcons.Itinerary,
                    onClick = {
                        closeSidePanel()
                        navigator.safePush(
                            EditItineraryScreenNav(
                                itineraryId = editItineraryState.getAllItinerary.data.orEmpty().first().id,
                            ),
                        )
                    },
                    modifier = Modifier.fillMaxWidth(),
                )
            }

            Spacer(modifier = Modifier.height(1.spacing))

            ItineraryServicesList(
                state = editItineraryState,
                onEvent = editItineraryScreenModel::onEvent,
            )
        }
    }
}
